import { useState } from 'react';
import { Alert, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { FaKey, FaPlug } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import i18n from '@/utils/i18n';
import { Communicator } from '../../tools/Communication';
import { INTEGRATION_SERVICES } from './integrationServices';

// The generic "other" service is superseded by named custom integrations, so it is not
// offered as a card anymore.
const GENERAL_API_KEY_SERVICE = 'other';

type AddIntegrationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
  // From GetIntegrations: which services can be added ("test" only on dev)
  availableServices: string[];
  addedServices: string[];
  onAdded: () => Promise<void> | void;
};

export const AddIntegrationModal = ({
  isOpen,
  onClose,
  storeId,
  availableServices,
  addedServices,
  onAdded,
}: AddIntegrationModalProps) => {
  const [busy, setBusy] = useState(false);
  const [customName, setCustomName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const post = async (body: Record<string, unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await Communicator.PostData('/Integration/AddIntegration', {
        storeId,
        ...body,
      });
      await onAdded();
      onClose();
    } catch (e) {
      try {
        const errorBody = await (e as Response).json();
        setError(errorBody.errorMessage);
      } catch {
        setError(i18n.t('Integrations:error'));
      }
    }
    setBusy(false);
  };

  const renderCard = (service: string) => {
    const serviceInfo = INTEGRATION_SERVICES.find((s) => s.service === service);
    const isAdded = addedServices.includes(service);
    const title = i18n.t(`Integrations:${serviceInfo?.i18nKey ?? service}`);

    return (
      <div className="col-md-6" key={service}>
        <button
          type="button"
          className="btn h-100 w-100 rounded border p-3 text-start"
          disabled={isAdded || busy}
          onClick={() => post({ service })}
        >
          <div className="d-flex">
            <div className="align-self-center text-muted">
              <FaPlug size={24} />
            </div>
            <div className="ms-3">
              <strong>{title}</strong>
              {isAdded && (
                <span className="badge bg-secondary ms-2">
                  {i18n.t('Integrations:alreadyAdded')}
                </span>
              )}
              <div className="text-muted small">
                {i18n.t('Integrations:cardDescription', { name: title })}
              </div>
            </div>
          </div>
        </button>
      </div>
    );
  };

  const brandedServices = availableServices.filter(
    (service) => service !== GENERAL_API_KEY_SERVICE,
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {i18n.t('Integrations:addIntegration')}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        <div className="mb-3 rounded border p-3">
          <div className="d-flex mb-2">
            <div className="align-self-center text-muted">
              <FaKey size={24} />
            </div>
            <div className="ms-3">
              <strong>{i18n.t('Integrations:customIntegrationTitle')}</strong>
              <div className="text-muted small">
                {i18n.t('Integrations:customIntegrationDescription')}
              </div>
            </div>
          </div>
          {/* data-no-bs: shadcn Input/Button inside the modal-root's Bootstrap scope. */}
          <div className="tw:flex tw:gap-2" data-no-bs>
            <Input
              type="text"
              value={customName}
              placeholder={i18n.t(
                'Integrations:customIntegrationNamePlaceholder',
              )}
              disabled={busy}
              onChange={(e) => setCustomName(e.target.value)}
            />
            <Button
              disabled={busy || customName.trim() === ''}
              onClick={() => post({ name: customName.trim() })}
            >
              {i18n.t('Integrations:addIntegration')}
            </Button>
          </div>
        </div>

        <div className="row g-3">
          {brandedServices.map((service) => renderCard(service))}
        </div>
      </ModalBody>
    </Modal>
  );
};
